import SpeakButton from "../EnglishTool/SpeakButton";
import { getReadingPool } from "../EnglishTool/pool/readingPool.js";
import { getSentencePool } from "../EnglishTool/pool/sentencePool.js";

export const chapterPages = {
    1: [
      {
        title: <h2>Noun Clauses & Nominalization Mastery</h2>,
        body: (
          <>
            <p>
              <b>Noun Clauses:</b> These are dependent clauses acting as nouns, often beginning with <i>that, what, who, whether, why, how</i>.<br />
              <b>Subject:</b> <i><b>What you said</b> was surprising.</i>
              <SpeakButton text="What you said was surprising." /><br />
              <b>Object:</b> <i>I wonder <b>how the story ends</b>.</i>
              <SpeakButton text="I wonder how the story ends." />
            </p>
            <p>
              <b>Try it!</b> Identify the noun clause:<br />
              <i>She didn’t know <b>who had called her.</b></i>
              <SpeakButton text="She didn’t know who had called her." />
            </p>
            <p>
              <b>Nominalization:</b> Turning verbs/adjectives into nouns for academic style.<br />
              <i>"They decided quickly." → "Their <b>decision</b> was prompt."</i>
              <SpeakButton text="Their decision was prompt." />
              <br />
              <b>Challenge:</b> Change this verb to a noun: "He failed." → ?
            </p>
            <p>
              <b>Embedded Appositives:</b> Appositives add extra info about a noun.<br />
              <i>The book, <b>a bestseller</b>, changed her life.</i>
              <SpeakButton text="The book, a bestseller, changed her life." />
              <br />
              <b>Try it!</b> Spot the appositive: "My friend, <b>a talented artist</b>, designed this cover."
            </p>
            <p>
              <b>Mini Reading:</b>
              <br />
              <i>
                {getReadingPool(8)[0]?.statement}
              </i>
              <br />
              <b>What is the main idea?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Theme, Motif & Extended Metaphor (in Context)</h2>,
        body: (
          <>
            <p>
              <b>Theme:</b> The central message of a work.<br />
              <b>Motif:</b> A recurring element that supports the theme.<br />
              <b>Example:</b> <i>Darkness appears as a motif for fear and the unknown.</i>
              <SpeakButton text="Darkness appears throughout the novel as a motif for fear and the unknown." />
            </p>
            <p>
              <b>Extended Metaphor:</b> A comparison developed over several lines or the whole text.<br />
              <b>Example:</b> <i>Life is compared to a journey through winding roads and obstacles.</i>
              <SpeakButton text="Life is compared to a journey through winding roads and obstacles." />
            </p>
            <p>
              <b>Mini Analysis:</b> What is the motif in: <br />
              <i>"Rain taps the window every night, reminding her of home."</i>
              <SpeakButton text="Rain taps the window every night, reminding her of home." />
            </p>
            <p>
              <b>Challenge:</b> Identify the theme and motif:<br />
              <i>
                "Her footsteps echoed in the empty hall, a constant reminder of loss."
                <SpeakButton text="Her footsteps echoed in the empty hall, a constant reminder of loss." />
              </i>
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Complex Noun Clauses & Literary Devices</h2>,
        body: (
          <>
            <p>
              <b>Find the noun clause, nominalization, and appositive:</b>
              <br />
              "That she excelled in the competition, an unexpected result, led to her recognition."
              <SpeakButton text="That she excelled in the competition, an unexpected result, led to her recognition." />
            </p>
            <p>
              <b>Try it!</b> Write your own sentence using a noun clause and an appositive.
            </p>
            <p>
              <b>Challenge:</b> Write a sentence using an extended metaphor and a noun clause.<br />
              <i>Example: How he navigated his challenges became a map, guiding others through storms.</i>
              <SpeakButton text="How he navigated his challenges became a map, guiding others through storms." />
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(8)[1]?.statement}
              <br />
              <b>What is the noun clause in the following?</b> "Whatever you choose will make a difference."
              <SpeakButton text="Whatever you choose will make a difference." />
            </p>
          </>
        ),
      },
    ],
  
    2: [
      {
        title: <h2>Verb Voice, Mood, & Real-World Usage</h2>,
        body: (
          <>
            <p>
              <b>Active Voice:</b> The subject does the action.<br />
              <i>The scientist conducted the experiment.</i>
              <SpeakButton text="The scientist conducted the experiment." /><br />
              <b>Passive Voice:</b> The subject receives the action.<br />
              <i>The experiment was conducted by the scientist.</i>
              <SpeakButton text="The experiment was conducted by the scientist." />
            </p>
            <p>
              <b>Try it!</b> Change to passive: "Students solved the problem."
            </p>
            <p>
              <b>Mood:</b> <br />
              <ul>
                <li><b>Indicative:</b> States fact ("He writes poems.")</li>
                <li><b>Imperative:</b> Command ("Write a poem.")</li>
                <li><b>Interrogative:</b> Question ("Did you write a poem?")</li>
                <li><b>Conditional:</b> If/then ("If I wrote a poem, I would share it.")</li>
                <li><b>Subjunctive:</b> Hypothetical ("If I were a poet, I would travel the world.")</li>
              </ul>
              <SpeakButton text="If I were a poet, I would travel the world." />
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(8)[2]?.statement}
              <br />
              <b>What verb(s) show the main action?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Sentence Types, Fragments & Run-Ons</h2>,
        body: (
          <>
            <p>
              <b>Fragments:</b> Incomplete sentences.<br />
              <i>"When the rain stopped."</i>
              <SpeakButton text="When the rain stopped." />
            </p>
            <p>
              <b>Run-On Sentences:</b> Multiple independent clauses without proper punctuation.<br />
              <i>"He wrote a poem he forgot to share it."</i>
              <SpeakButton text="He wrote a poem he forgot to share it." />
            </p>
            <p>
              <b>Try it!</b> Correct the run-on: "She finished the book she started another one."
            </p>
            <p>
              <b>Practice:</b> Make your own run-on and fix it.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Verbs & Sentences in Context</h2>,
        body: (
          <>
            <p>
              <b>Find all verbs:</b><br />
              <i>{getSentencePool(8).find(s => s.focus === "verbs")?.sentence}</i>
            </p>
            <p>
              <b>Convert to passive voice:</b><br />
              <i>"The committee announced the results."</i>
              <SpeakButton text="The committee announced the results." />
            </p>
            <p>
              <b>Challenge:</b> Write a compound-complex sentence about your school life.
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(8)[3]?.statement}
              <br />
              <b>Which verb best describes the main action?</b>
            </p>
          </>
        ),
      },
    ],
  
    3: [
      {
        title: <h2>Nuanced Adjectives & Powerful Imagery</h2>,
        body: (
          <>
            <p>
              <b>Nuanced Adjectives:</b> Specific, vivid adjectives make writing come alive.<br />
              <i>The <b>melancholy</b> autumn evening glowed with <b>faint</b> golden light.</i>
              <SpeakButton text="The melancholy autumn evening glowed with faint golden light." />
            </p>
            <p>
              <b>Imagery:</b> Words that appeal to senses.<br />
              <i>A <b>fragrant</b> breeze swept over the <b>silent</b> fields.</i>
              <SpeakButton text="A fragrant breeze swept over the silent fields." />
            </p>
            <p>
              <b>Try it!</b> Pick an object in your room and describe it using two adjectives.
            </p>
            <p>
              <b>Mini Analysis:</b> What is the adjective in:<br />
              <i>{getSentencePool(8).find(s => s.focus === "adjectives")?.sentence}</i>
            </p>
          </>
        ),
      },
      {
        title: <h2>Comparative, Superlative & Figurative Adjectives</h2>,
        body: (
          <>
            <p>
              <b>Comparative & Superlative:</b> "more/most" or "-er/-est" forms.<br />
              <i>She is more resourceful than her classmates and the most creative in the group.</i>
              <SpeakButton text="She is more resourceful than her classmates and the most creative in the group." />
            </p>
            <p>
              <b>Figurative Adjectives:</b> Used in metaphors or personification.<br />
              <i>A <b>restless</b> wind swept through the city.</i>
              <SpeakButton text="A restless wind swept through the city." />
            </p>
            <p>
              <b>Try it!</b> Write a sentence comparing two friends with an adjective.
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(8)[4]?.statement}
              <br />
              <b>Which adjective could describe the subject?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Adjectives & Imagery</h2>,
        body: (
          <>
            <p>
              <b>Write a descriptive paragraph about a place, using at least three adjectives and one metaphor.</b>
            </p>
            <p>
              <b>Find the adjectives:</b><br />
              <i>{getSentencePool(8).filter(s => s.focus === "adjectives")[1]?.sentence}</i>
            </p>
            <p>
              <b>Challenge:</b> Identify the imagery and figurative adjective in:<br />
              <b>"The silver moon stared down at the anxious travelers crossing the endless desert."</b>
              <SpeakButton text="The silver moon stared down at the anxious travelers crossing the endless desert." />
            </p>
          </>
        ),
      },
    ],
  
    4: [
      {
        title: <h2>Adverbs, Transitions & Media Literacy</h2>,
        body: (
          <>
            <p>
              <b>Adverbial Phrases:</b> Groups of words that act as adverbs.<br />
              <i>He spoke <b>with great conviction</b>.</i>
              <SpeakButton text="He spoke with great conviction." />
            </p>
            <p>
              <b>Try it!</b> Make a sentence using an adverb phrase.
            </p>
            <p>
              <b>Transitions & Conjunctive Adverbs:</b> Used to connect ideas.<br />
              <i>"She completed her work; <b>therefore</b>, she could relax."</i>
              <SpeakButton text="She completed her work; therefore, she could relax." />
            </p>
            <p>
              <b>Media Literacy:</b> Recognize techniques like bandwagon, emotional appeal, testimonial, repetition.<br />
              <i>Example: "Join everyone who’s switching to EcoWater!" (bandwagon)</i>
              <SpeakButton text="Join everyone who’s switching to EcoWater!" />
            </p>
          </>
        ),
      },
      {
        title: <h2>Analyzing Persuasive Devices & Advanced Adverbs</h2>,
        body: (
          <>
            <p>
              <b>Emotional Appeal:</b> Using emotions to influence.<br />
              <i>"Don't let your family down—buy SafeHome Insurance."</i>
              <SpeakButton text="Don't let your family down—buy SafeHome Insurance." />
            </p>
            <p>
              <b>Testimonial:</b> Using a celebrity or expert recommendation.<br />
              <i>"Top athletes choose EnergyBar for success."</i>
              <SpeakButton text="Top athletes choose EnergyBar for success." />
            </p>
            <p>
              <b>Find the adverb:</b><br />
              <i>{getSentencePool(8).find(s => s.focus === "adverbs")?.sentence}</i>
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(8)[2]?.statement}
              <br />
              <b>Which adverb could describe the action?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Adverbs, Media, & Real-World Sentences</h2>,
        body: (
          <>
            <p>
              <b>Write a sentence with an adverbial phrase and a conjunctive adverb.</b>
              <br />
              <i>Example: After a long day, she completed her project; therefore, she felt proud.</i>
              <SpeakButton text="After a long day, she completed her project; therefore, she felt proud." />
            </p>
            <p>
              <b>Find the adverb(s):</b><br />
              <i>{getSentencePool(8).filter(s => s.focus === "adverbs")[1]?.sentence}</i>
            </p>
            <p>
              <b>Challenge:</b> Spot the adverbial phrase:<br />
              <b>"With quiet determination, he approached the daunting challenge."</b>
              <SpeakButton text="With quiet determination, he approached the daunting challenge." />
            </p>
          </>
        ),
      },
    ]
  };